import { useState } from 'react';
import type { Product } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { BigButton } from '../BigButton.js';
import { SegmentedControl } from '../SegmentedControl.js';

export interface StockAdjustDialogProps {
  product: Product;
  onClose: () => void;
  onAdjusted: (product: Product) => void;
}

type Reason = 'restock' | 'adjustment';
type Direction = 'add' | 'remove';

const STEPPER_BUTTON_CLASS =
  'h-12 w-12 shrink-0 rounded-lg bg-aqua text-teal-dark font-heading font-bold text-xl flex items-center justify-center disabled:opacity-40';

/**
 * The restock / adjustment sheet, used from both the products list and the
 * product edit page.
 *
 * The quantity is always typed as a positive whole number; a separate
 * Add/Remove control decides the sign. This replaced an earlier design where
 * "Adjustment" required typing a leading `-` to reduce stock, which is
 * impossible on a phone numeric keypad (no minus key, spinner arrows don't
 * render on mobile) - see docs/CHANGELOG.md.
 */
export function StockAdjustDialog({ product, onClose, onAdjusted }: StockAdjustDialogProps) {
  const [reason, setReason] = useState<Reason>('restock');
  const [direction, setDirection] = useState<Direction>('add');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Restock is always an increase; only Adjustment lets the user pick a
  // direction. Guard against stale state even though we also reset it on
  // the segmented control's onChange below.
  const effectiveDirection: Direction = reason === 'restock' ? 'add' : direction;

  const qty = quantity === '' ? null : Number(quantity);
  const sign = effectiveDirection === 'add' ? 1 : -1;
  const delta = qty !== null ? sign * qty : null;
  const resultingStock = product.stockQty + (delta ?? 0);

  const removalError =
    effectiveDirection === 'remove' && qty !== null && qty > product.stockQty
      ? `Cannot remove more than ${product.stockQty} in stock`
      : null;

  const displayError = removalError ?? apiError;
  const canSubmit = qty !== null && qty > 0 && !removalError && !submitting;
  const canDecrement = qty !== null && qty > 1 && !submitting;

  function handleQuantityChange(value: string) {
    setQuantity(value.replace(/[^0-9]/g, ''));
    setApiError(null);
  }

  function handleDecrement() {
    setQuantity((prev) => {
      const n = prev === '' ? 1 : Number(prev);
      return String(Math.max(1, n - 1));
    });
    setApiError(null);
  }

  function handleIncrement() {
    setQuantity((prev) => {
      const n = prev === '' ? 0 : Number(prev);
      return String(n + 1);
    });
    setApiError(null);
  }

  async function handleSubmit() {
    if (delta === null || delta === 0 || removalError) return;
    setSubmitting(true);
    setApiError(null);
    try {
      const updated = await api.adjustStock(product.id, {
        delta,
        reason,
        note: note.trim() || undefined,
      });
      onAdjusted(updated);
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not update stock');
    } finally {
      setSubmitting(false);
    }
  }

  const quantityLabel = effectiveDirection === 'add' ? 'Quantity to add' : 'Quantity to remove';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/40 sm:p-6"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="stock-adjust-title"
        className="w-full max-w-sm bg-surface rounded-xl shadow-xl p-4 sm:p-6 flex flex-col gap-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 id="stock-adjust-title" className="font-heading font-semibold text-lg text-ink">
            Update stock &middot; {product.code}
          </h2>
        </div>

        <SegmentedControl
          ariaLabel="Reason"
          value={reason}
          onChange={(v) => {
            setReason(v);
            if (v === 'restock') setDirection('add');
            setApiError(null);
          }}
          options={[
            { value: 'restock', label: 'Restock' },
            { value: 'adjustment', label: 'Adjustment' },
          ]}
        />

        {reason === 'adjustment' && (
          <SegmentedControl
            ariaLabel="Direction"
            value={direction}
            onChange={(v) => {
              setDirection(v);
              setApiError(null);
            }}
            options={[
              { value: 'add', label: 'Add' },
              { value: 'remove', label: 'Remove' },
            ]}
          />
        )}

        <div>
          <label htmlFor="stock-qty" className="block font-body font-semibold text-base text-teal-dark mb-1">
            {quantityLabel}
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDecrement}
              disabled={!canDecrement}
              aria-label="Decrease quantity"
              className={STEPPER_BUTTON_CLASS}
            >
              &minus;
            </button>
            <input
              id="stock-qty"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={quantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              disabled={submitting}
              placeholder="0"
              className="w-full min-h-12 rounded-lg border-2 border-border px-4 text-lg font-heading text-center"
            />
            <button
              type="button"
              onClick={handleIncrement}
              disabled={submitting}
              aria-label="Increase quantity"
              className={STEPPER_BUTTON_CLASS}
            >
              +
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="stock-note" className="block font-body font-semibold text-base text-teal-dark mb-1">
            Note (optional)
          </label>
          <input
            id="stock-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={280}
            className="w-full min-h-12 rounded-lg border-2 border-border px-4 text-base font-body"
          />
        </div>

        <div className="font-body text-sm space-y-0.5">
          <p className="text-muted">Currently {product.stockQty} in stock</p>
          <p className="text-text">
            Resulting stock: <span className="font-heading font-semibold">{resultingStock}</span>
          </p>
        </div>

        {displayError && (
          <p role="alert" className="font-body text-sm text-danger">
            {displayError}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <BigButton variant="outline" onClick={onClose}>
            Cancel
          </BigButton>
          <BigButton disabled={!canSubmit} onClick={() => void handleSubmit()}>
            {submitting ? 'Saving…' : 'Save'}
          </BigButton>
        </div>
      </div>
    </div>
  );
}
